// src/screens/pass/PassPurchaseStepView.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  Space,
  Text,
  Title,
} from "@mantine/core";

import PassPurchaseBlock from "./PassPurchaseBlock";
import StripePayModal from "./StripePaymentModal";

import {
  createSitePass,
  extendSitePass,
  getCurrentPass,
  getSitePassExtendPaymentIntent,
  getSitePassPaymentIntent,
} from "../../atoms/passNetwork";

function formatCurrency(value) {
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
}

function calculatePricing(nights, passes) {
  if (!nights || nights <= 0 || !Array.isArray(passes) || passes.length === 0) {
    return { total: 0, components: [], extraNights: 0, extraNightRate: 0 };
  }

  const sorted = [...passes].sort((a, b) => a.duration - b.duration);
  const base = sorted[0];
  const singleNightRate = base?.duration > 0 ? base.cost / base.duration : 0;

  // exact match
  const exact = sorted.find((p) => p.duration === nights);
  if (exact) {
    return {
      total: exact.cost,
      components: [
        { title: exact.title, duration: exact.duration, count: 1, cost: exact.cost },
      ],
      extraNights: 0,
      extraNightRate: singleNightRate,
    };
  }

  // DP (same idea as Swift)
  const tariffs = [
    ...sorted.map((p) => ({ duration: p.duration, cost: p.cost, pass: p })),
    { duration: 1, cost: singleNightRate, pass: null }, // extra single nights
  ];

  const maxN = nights;
  const dp = Array(maxN + 1).fill(Number.POSITIVE_INFINITY);
  const choice = Array(maxN + 1).fill(-1);
  dp[0] = 0;

  for (let day = 1; day <= maxN; day++) {
    for (let i = 0; i < tariffs.length; i++) {
      const t = tariffs[i];
      if (t.duration <= day && dp[day - t.duration] + t.cost < dp[day]) {
        dp[day] = dp[day - t.duration] + t.cost;
        choice[day] = i;
      }
    }
  }

  // reconstruct
  const counts = Array(tariffs.length).fill(0);
  let d = maxN;
  while (d > 0 && choice[d] >= 0) {
    const idx = choice[d];
    counts[idx] += 1;
    d -= tariffs[idx].duration;
  }

  let total = 0;
  let extraNights = 0;
  const components = [];

  counts.forEach((count, idx) => {
    if (count <= 0) return;
    const t = tariffs[idx];
    const subtotal = count * t.cost;
    total += subtotal;

    if (t.duration === 1 && !t.pass) {
      extraNights = count;
    } else if (t.pass) {
      components.push({
        title: t.pass.title,
        duration: t.pass.duration,
        count,
        cost: subtotal,
      });
    }
  });

  return { total, components, extraNights, extraNightRate: singleNightRate };
}

export default function PassPurchaseStepView({
  siteId,
  name,
  phoneE164,
  registrationNumber,
  address,
  setRegistrationNumber,
  setAddress,
  extendPassId,
  setExtendPassId,
  passCode,
  setPassCode,
  maxDuration,
  passes,
  onConfirmPayment,
  onBack,
}) {
  const isExtending = Number(extendPassId || 0) !== 0;

  const [nights, setNights] = useState(3);
  const [existingPassDuration, setExistingPassDuration] = useState(0);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const [paying, setPaying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Stripe modal state
  const [showStripe, setShowStripe] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [transactionId, setTransactionId] = useState(null);
  const [finalizing, setFinalizing] = useState(false);

  const pricing = useMemo(() => calculatePricing(nights, passes), [nights, passes]);

  const singleNightRate = useMemo(() => {
    if (!passes?.length) return 0;
    const base = [...passes].sort((a, b) => a.duration - b.duration)[0];
    return base?.duration > 0 ? base.cost / base.duration : 0;
  }, [passes]);

  const maxExtraDaysAllowed = useMemo(() => {
    if (!isExtending) return Number(maxDuration || 1);
    return Math.max(0, Number(maxDuration || 1) - Number(existingPassDuration || 0));
  }, [isExtending, maxDuration, existingPassDuration]);

  const purchaseBlockMaxDuration = useMemo(() => {
    return isExtending ? Math.max(maxExtraDaysAllowed, 1) : Number(maxDuration || 1);
  }, [isExtending, maxExtraDaysAllowed, maxDuration]);

  const canPay = useMemo(() => {
    return (
      pricing.total > 0 &&
      passes?.length > 0 &&
      (!isExtending || maxExtraDaysAllowed > 0) &&
      !loadingExisting &&
      !finalizing
    );
  }, [pricing.total, passes, isExtending, maxExtraDaysAllowed, loadingExisting, finalizing]);

  const appliedPassesPayload = useMemo(() => {
    if (!pricing?.components?.length) return [];
    const out = [];
    for (const c of pricing.components) {
      const p = (passes || []).find((x) => x.duration === c.duration && x.title === c.title);
      if (p?.id) out.push({ id: p.id, count: c.count });
    }
    return out;
  }, [pricing.components, passes]);

  useEffect(() => {
    // initial nights = min duration pass
    if (!passes?.length) return;
    const minDur = Math.max(1, Math.min(...passes.map((p) => Number(p.duration || 1))));
    setNights(minDur);
  }, [passes]);

  useEffect(() => {
    async function loadExisting() {
      if (!isExtending) return;
      setLoadingExisting(true);
      setErrorMsg("");

      try {
        const res = await getCurrentPass({ passId: extendPassId });
        const ok = Number(res?.status) === 1;
        const data = res?.data || {};
        if (!ok) throw new Error(data?.message || "Unable to load your current pass.");

        const pass = data?.pass || {};
        const currentDuration = Number(pass?.duration || 0);
        setExistingPassDuration(currentDuration);

        if (pass?.code) setPassCode(String(pass.code));
        if (pass?.car_registration) setRegistrationNumber(String(pass.car_registration));
        if (pass?.car_address) setAddress(String(pass.car_address));

        const remaining = Math.max(0, Number(maxDuration || 1) - currentDuration);
        setNights(remaining <= 0 ? 1 : Math.min(Math.max(nights, 1), remaining));
      } catch (e) {
        setErrorMsg(e?.message || "Unable to load your current pass.");
      } finally {
        setLoadingExisting(false);
      }
    }

    loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExtending, extendPassId]);

  async function startPayment() {
    if (!canPay) return;

    setPaying(true);
    setErrorMsg("");

    try {
      // 1) fetch payment intent
      const { clientSecret, transactionId } = isExtending
        ? await getSitePassExtendPaymentIntent({
          siteId,
          phoneE164,
          passes: appliedPassesPayload,
          duration: nights,
          total: pricing.total,
        })
        : await getSitePassPaymentIntent({
          siteId,
          phoneE164,
          passes: appliedPassesPayload,
          duration: nights,
          total: pricing.total,
        });

      setClientSecret(clientSecret);
      setTransactionId(transactionId);
      setShowStripe(true);
    } catch (e) {
      setErrorMsg(e?.message || "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  }

  async function finalizePassAfterPaid() {
    if (!transactionId) {
      setErrorMsg("Missing transaction. Please try again.");
      return;
    }

    setFinalizing(true);
    setErrorMsg("");

    try {
      const res = isExtending
        ? await extendSitePass({
          phoneE164,
          duration: nights,
          transactionId,
          carRegistration: registrationNumber,
          carAddress: address,
          passId: extendPassId,
        })
        : await createSitePass({
          siteId,
          phoneE164,
          duration: nights,
          transactionId,
          carRegistration: registrationNumber,
          carAddress: address,
          extendPassId: extendPassId || 0,
        });

      const ok = Number(res?.status) === 1;
      const data = res?.data || {};
      if (!ok) throw new Error(data?.message || "Unable to create your pass. Please try again.");

      let pass = data?.pass;
      if (!pass) throw new Error("Pass created, but details were missing. Please contact support.");

      pass.cost = pass.cost / 100;

      onConfirmPayment(pass);
    } catch (e) {
      setErrorMsg(e?.message || "Payment succeeded, but pass creation failed. Please contact support.");
    } finally {
      setFinalizing(false);
    }
  }

  return (
    <Container size={700} px="md" style={{ minHeight: "100vh", paddingBottom: 120 }}>
      <Space h={24} />

      <Title order={2}>{isExtending ? "Extend your pass" : "Review your passes"}</Title>
      <Text c="dimmed" mt={6}>
        {isExtending
          ? "Choose how many extra days you’d like to add, then complete payment."
          : "Check your details and stay length before completing payment."}
      </Text>

      {isExtending ? (
        <Paper withBorder radius="lg" p="md">
          <Text fw={700}>Extending an existing pass</Text>
          <Text size="sm" c="dimmed">
            Pass Code: {passCode || "—"}
          </Text>
          <Text size="sm" c="dimmed" mt={6}>
            Current duration: {existingPassDuration} night(s) • You can add up to {maxExtraDaysAllowed} more night(s)
          </Text>
        </Paper>
      ) : null}

      <Space h="md" />

      <Paper withBorder radius="lg" p="md">
        <Text fw={600} size="sm">
          Your details
        </Text>

        <Space h="sm" />
        {name ? (
          <Text size="sm">
            <Text span fw={700}>Name:</Text> {name}
          </Text>
        ) : null}

        {phoneE164 ? (
          <Text size="sm">
            <Text span fw={700}>Phone:</Text> {phoneE164}
          </Text>
        ) : null}

        {registrationNumber ? (
          <Text size="sm">
            <Text span fw={700}>Registration Number:</Text> {registrationNumber}
          </Text>
        ) : null}

        {address ? (
          <Text size="sm">
            <Text span fw={700}>Address:</Text> {address}
          </Text>
        ) : null}
      </Paper>

      <Space h="md" />

      <Text fw={600} size="sm">
        {isExtending ? "Extension Length" : "Stay Length"}
      </Text>

      <Space h="xs" />

      <PassPurchaseBlock
        nights={nights}
        setNights={setNights}
        nightlyRate={singleNightRate}
        maxDuration={purchaseBlockMaxDuration}
      />

      <Space h="md" />

      <Paper withBorder radius="lg" p="md">
        <Text fw={600} size="sm" mb={8}>
          Applied passes
        </Text>

        {pricing.components.length === 0 ? (
          <Text size="sm" c="dimmed">
            No passes applied yet.
          </Text>
        ) : (
          <>
            {pricing.components.map((c, idx) => (
              <Group
                key={`${c.title}-${idx}`}
                justify="space-between"
                align="flex-start"
                mt={6}
              >
                <div>
                  <Text size="sm">
                    {c.duration}-night pass
                    {c.count > 1 ? ` × ${c.count}` : ""}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {c.title}
                  </Text>
                </div>

                <Text size="sm" fw={700}>
                  {formatCurrency(c.cost)}
                </Text>
              </Group>
            ))}

            {pricing.extraNights > 0 ? (
              <Group justify="space-between" align="flex-start" mt={6}>
                <div>
                  <Text size="sm">
                    Extra night{pricing.extraNights > 1 ? "s" : ""} × {pricing.extraNights}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Additional nightly rate
                  </Text>
                </div>

                <Text size="sm" fw={700}>
                  {formatCurrency(pricing.extraNights * pricing.extraNightRate)}
                </Text>
              </Group>
            ) : null}
          </>
        )}
      </Paper>

      <Space h="md" />

      <Paper withBorder radius="lg" p="md">
        <Text fw={600} size="sm" mb={8}>
          Available passes
        </Text>

        {[...(passes || [])]
          .sort((a, b) => a.duration - b.duration)
          .map((p) => (
            <Group key={p.id} justify="space-between" align="flex-start" mt={6}>
              <div>
                <Text size="sm">{p.duration}-night pass</Text>
                <Text size="xs" c="dimmed">
                  {p.title}
                </Text>
              </div>
              <Text size="sm" fw={700}>
                {formatCurrency(p.cost)}
              </Text>
            </Group>
          ))}
      </Paper>

      {errorMsg ? (
        <Text c="red" mt="md">
          {errorMsg}
        </Text>
      ) : null}

      <Button variant="subtle" mt="md" onClick={onBack}>
        Back
      </Button>

      <Box
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(255,255,255,0.92)",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          padding: 16,
          backdropFilter: "blur(10px)",
        }}
      >
        <Container size={700} px="md">
          <Group justify="space-between" align="center">
            <div>
              <Text size="xs" c="dimmed">
                Total
              </Text>
              <Text fw={800}>{formatCurrency(pricing.total)}</Text>
            </div>

            <Button
              size="md"
              onClick={startPayment}
              loading={paying}
              disabled={!canPay}
              style={{ minWidth: 200 }}
            >
              Pay now
            </Button>
          </Group>
        </Container>
      </Box>

      {/* Stripe modal */}
      <StripePayModal
        opened={showStripe}
        onClose={() => setShowStripe(false)}
        clientSecret={clientSecret}
        amount={pricing.total}   
        currency="aud"  
        onPaid={async () => {
          setShowStripe(false);
          await finalizePassAfterPaid();
        }}
      />
    </Container>
  );
}
